import { BaseType, drag, select, type Selection } from "d3";

import {
  calculateLineCoordinates,
  calculateRayCoordinates,
} from "@examspark/common/dist/utils/geom";
import { type Cartesian } from "@examspark/types";

import { WithIndex } from "../CartesianGraph";
import { D3Axis, roundToGrid } from "../d3Utils";

const colorDecorator = <GType extends BaseType>(
  d3SvgElement: Selection<GType, any, any, any>,
) => {
  d3SvgElement
    .attr("fill", "#1fa4e1")
    .attr("stroke", "#1fa4e1")
    .attr("stroke-width", 2);
};

export const createCoordinate = <GType extends BaseType>({
  x,
  y,
  onMove,
}: {
  x: D3Axis;
  y: D3Axis;
  onMove?: (
    shapeIndex: number,
    coordIndex: number,
    coord: Cartesian.Coordinate,
  ) => void;
}) => {
  return (point: Selection<GType, CoordWithIndex, any, any>) => {
    const dragHandleDecorator = drag<any, CoordWithIndex>()
      // subject is where we start, and needs to be in pixel space
      .subject((_, d) => ({
        x: x.scale(d.x),
        y: y.scale(d.y),
      }))
      .on("drag", (e, data) => {
        if (data.shapeIndex == null || data.index == null) {
          console.log("missing data props on drag");
          return;
        }
        const coordToSave = {
          x: roundToGrid(x.scale.invert(e.x), x),
          y: roundToGrid(y.scale.invert(e.y), y),
        };

        onMove?.(data.shapeIndex, data.index, coordToSave);
      });

    return point
      .call(colorDecorator)
      .attr("r", 23)
      .attr("cx", (coord) => x.scale(coord.x))
      .attr("cy", (coord) => y.scale(coord.y))
      .on("mouseover", function (d, i) {
        select(this)
          .transition()
          .duration(100)
          .attr("fill-opacity", 0.1)
          .attr("stroke-opacity", 1);
      })
      .on("mouseout", function (d, i) {
        select(this)
          .transition()
          .duration(300)
          .attr("fill-opacity", 0)
          .attr("stroke-opacity", 0);
      })
      .attr("fill-opacity", 0)
      .attr("stroke-opacity", 0)
      .attr("stroke-dasharray", "5,5")
      .call(dragHandleDecorator);
  };
};
const coordinatesForShape = (shape: Cartesian.Shape) =>
  "coords" in shape ? shape.coords : [shape.coord];

type CoordWithIndex = Cartesian.Coordinate & {
  shapeIndex?: number;
  index?: number;
};
export const useShapesDecorator =
  <GType extends BaseType>({
    x,
    y,
    onErase,
    onMove,
  }: {
    x: D3Axis;
    y: D3Axis;
    onErase?: (shapeIndex: number) => void;
    onMove?: (
      shapeIndex: number,
      coordIndex: number,
      coord: Cartesian.Coordinate,
    ) => void;
  }) =>
  (svg: Selection<GType, WithIndex<Cartesian.Shape>, any, any>) => {
    // append coordinate points first

    const shapeToCoords = (
      shape: WithIndex<Cartesian.Shape>,
      shapeIndex: number,
    ): CoordWithIndex[] =>
      coordinatesForShape(shape).map((coord, index) => ({
        ...coord,
        shapeIndex,
        index,
      }));

    svg
      .selectAll("circle.shape-coord-center")
      .data(shapeToCoords)
      .join("circle")
      .raise()
      .attr("class", "shape-coord-center")
      .attr("r", 4)
      .attr("cx", (coord) => x.scale(coord.x))
      .attr("cy", (coord) => y.scale(coord.y))
      .call(colorDecorator);

    svg
      .selectAll("circle.shape-coords")
      .data(shapeToCoords)
      .join("circle")
      .raise()
      .attr("class", "shape-coords")
      .call(createCoordinate({ x, y, onMove }))
      .on("click", (e, d) => {
        if (onErase && d.shapeIndex != null) {
          onErase(d.shapeIndex);
        }
      });

    // for each shape, connect the dots. This breaks out of d3 data joining,
    // because it _hates_ dynamic shapes, but it's ok because we are fine
    // removing/re-drawing each time (since we don't have any animations or
    // drag handles inside here)
    svg.each(function (shape) {
      const shapeSvg = select(this);

      shapeSvg.select(".outline").remove();
      const outlineSvg = shapeSvg.append("g").attr("class", "outline").lower();

      switch (shape.type) {
        case "point":
          break;
        case "polygon":
          outlineSvg
            .append("polygon")
            .attr(
              "points",
              shape.coords
                .map((coord) => `${x.scale(coord.x)},${y.scale(coord.y)}`)
                .join(" "),
            )
            .attr("fill-opacity", 0)
            .call(colorDecorator)
            .on("click", () => onErase?.(shape.index));
          break;
        case "circle": {
          const radius = Math.sqrt(
            Math.pow(shape.coords[1].x - shape.coords[0].x, 2) +
              Math.pow(shape.coords[1].y - shape.coords[0].y, 2),
          );

          // in order to account for grids of different scales (ie, (0..10, 0..100) )
          // and a desire to have a circle that makes mathematical sense, we need to
          // calculate the radius in cartesian space and scale it to the graph
          outlineSvg
            .append("ellipse")
            .attr("cx", x.scale(shape.coords[0].x))
            .attr("cy", y.scale(shape.coords[0].y))
            .attr("rx", Math.abs(x.scale(0) - x.scale(radius)))
            .attr("ry", Math.abs(y.scale(0) - y.scale(radius)))
            .attr("fill-opacity", 0)
            .call(colorDecorator)
            .on("click", () => onErase?.(shape.index));

          break;
        }
        case "dashed-line":
        case "line": {
          const coords = calculateLineCoordinates({
            shape,
            x,
            y,
          });

          if (coords) {
            outlineSvg
              .append("line")
              .attr("x1", x.scale(coords[0].x))
              .attr("y1", y.scale(coords[0].y))
              .attr("x2", x.scale(coords[1].x))
              .attr("y2", y.scale(coords[1].y))
              // add an arrow to the end of the line
              .style("marker-end", "url(#arrow)")
              .style("marker-start", "url(#arrow)")
              .attr(
                "stroke-dasharray",
                shape.type === "dashed-line" ? "5,5" : "",
              )
              .call(colorDecorator)
              .on("click", () => onErase?.(shape.index));
          }
          break;
        }
        case "ray": {
          const coords = calculateRayCoordinates({
            shape,
            x,
            y,
          });
          if (coords) {
            outlineSvg
              .append("line")
              .attr("x1", x.scale(coords[0].x))
              .attr("y1", y.scale(coords[0].y))
              .attr("x2", x.scale(coords[1].x))
              .attr("y2", y.scale(coords[1].y))
              .style("marker-end", "url(#arrow)")
              .call(colorDecorator)
              .on("click", () => onErase?.(shape.index));
          }

          break;
        }

        case "line-segment": {
          const line = outlineSvg
            .append("line")
            .attr("x1", x.scale(shape.coords[0].x))
            .attr("y1", y.scale(shape.coords[0].y))
            .attr("x2", x.scale(shape.coords[1].x))
            .attr("y2", y.scale(shape.coords[1].y));

          colorDecorator(line);
          // eraseDecorator(shapeIndex)(line);
          break;
        }
      }
    });
  };
